
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { configApi } from '../../../lib/apiClient';
import styles from './ConnectionsCard.module.css';
import { Settings } from 'lucide-react';
import { getTypeMetaByConnectionConfigId } from '../../../utils/utils';
import { ConnectionConfig } from '../../../types/types';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { User } from '../../../types/user';
import useEmblaCarousel from 'embla-carousel-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import Loader from '../../../ui/Loader/Loader';
import { TagsEnv } from '../../../ui/Tags/TagsEnv';


export default function ConnectionsCard({ user }: { user: User | null; }) {
  const [emblaRef] = useEmblaCarousel({ dragFree: true, containScroll: 'trimSnaps' });
  const [initialTestDone, setInitialTestDone] = useState(false);
  const [testing, setTesting] = useState<boolean>(false);
  const { connectors, connectorsLoading, connectionsStatus, setConnectionStatus, isLoadingConnectors } = useConnectionsStore(state => state)

  const filteredConnections = connectors

  const filteredConnectionsStatus = connectionsStatus.filter(status => {
    return connectors?.find(c => c.id === status.id);
  });

  useEffect(() => {
    if (!filteredConnections.length) return;
    if (!initialTestDone) {
      testConnections(filteredConnections);
      setInitialTestDone(true);
    }
  }, [connectors.length]);


  const testConnections = async (connectors: ConnectionConfig[]) => {
    setTesting(true);
    try {
      const bulkResults = await configApi.testConnectionsBulk(connectors.map(c => c.id));
      const resultsById = new Map(bulkResults.map(r => [r.id, r]));
      const results = connectors.map((connection) => {
        const result = resultsById.get(connection.id);
        const status = result?.success ? 'Connected' : (result ? 'Failed' : 'Error');
        return { id: connection.id, name: connection.connectionName || '', url: connection.config?.baseUrl || '', status, env: connection.environment || 'prod' };
      });
      setConnectionStatus(results);
    } catch (error) {
      setConnectionStatus(connectors.map(connection => ({ id: connection.id, name: connection.connectionName || '', url: connection.config?.baseUrl || '', status: 'Error', env: connection.environment || 'prod' })));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>Connectors</h2>
        <button
          className={styles.iconButton}
          onClick={() => testConnections(filteredConnections)}
          disabled={testing}
          title="Refresh Connection Status"
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={styles.refreshIcon} xmlns="http://www.w3.org/2000/svg">
            <path d="M17.65 6.35A8 8 0 1 0 20 12h-1.5a6.5 6.5 0 1 1-1.85-6.15l-2.15 2.15H20V4l-2.35 2.35z" fill="currentColor" />
          </svg>
          <span style={{ marginLeft: 4 }}>{testing ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>
      {connectorsLoading || testing || isLoadingConnectors || (connectors.length > 0 && !initialTestDone) ? (
        <div className={styles.loadingRow}>
          <Loader />
        </div>
      ) :
        connectors?.length === 0 ? (
          <div>
            <p>No connectors configured.</p>
            {hasMinRole(user?.role?.id, UserRole.Admin) && (
              <Link to="/connectors" className={styles.iconButton}>
                <Settings size={16} />
                <span>Configure Connections</span>
              </Link>
            )}
          </div>
        ) : (
              <div className={styles.embla} ref={emblaRef}>
                <div className={styles.embla__container}>
                  {filteredConnectionsStatus?.map((config) => {
                    const meta = getTypeMetaByConnectionConfigId(config.id, connectors);
                    return hasMinRole(user?.role?.id, UserRole.Admin) ? (
                      <div key={config.id} className={styles.connectionInfo + ' ' + styles.embla__slide}>
                        <TagsEnv env={config.env} isToolDisabledByConnector={false} className={styles.tagsEnv} />
                        <div>
                          <img className={styles.icon} src={meta?.icon} alt={meta?.title} />
                          <p><b>{config.name || meta?.title || 'Unknown'}</b></p>
                        </div>
                        <p>
                          <b>Status:</b>{' '}
                          <span className={
                            config.status === 'Connected' ? styles.statusSuccess :
                              config.status === 'Failed' ? styles.statusError :
                                styles.statusUnknown
                          }>
                            {config.status || 'Checking...'}
                          </span>
                        </p>
                        <div className={styles.buttonGroup}>
                          <Link to={`${meta?.route}/${config.id}`} state={{ from: '/dashboard' }} className={styles.iconButtonLink}>
                            <Settings size={16} />
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div key={config.id} className={styles.connectionInfo + ' ' + styles.embla__slide}>
                        <img className={styles.icon} src={meta?.icon} alt={meta?.title} />
                        <p><b>{meta?.title || 'Unknown'}</b></p>
                        <p>
                          <b>Status:</b>{' '}
                          <span className={
                            config.status === 'Connected' ? styles.statusSuccess :
                              config.status === 'Failed' ? styles.statusError :
                                styles.statusUnknown
                          }>
                            {config.status}
                          </span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
    </div>
  );
}
